// src/App.js

import React, { useState, useEffect } from 'react';
import charactersData from './data/characters.json';
import {
  selectDailyCharacter,
  selectYesterdayCharacter,
} from './utils/selectDailyCharacter';
import GuessInput from './components/GuessInput';
import GuessHistory from './components/GuessHistory';
import CongratulationsDialog from './components/CongratulationsDialog';

function App() {
  const [answer, setAnswer] = useState(null);
  const [yesterdayAnswer, setYesterdayAnswer] = useState(null);
  const [guessedCharacters, setGuessedCharacters] = useState([]);
  const [enlargedImageSrc, setEnlargedImageSrc] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const characters = Object.values(charactersData);

  // Filter characters to only include those with guessable = true
  const guessableCharacters = characters.filter(
    (char) => char.guessable === true
  );

  useEffect(() => {
    const dailyCharacter = selectDailyCharacter(guessableCharacters);
    const yesterdayCharacter = selectYesterdayCharacter(guessableCharacters);
    setAnswer(dailyCharacter);
    setYesterdayAnswer(yesterdayCharacter);
  }, [guessableCharacters]);

  const handleGuess = (guessName) => {
    const guessedCharacter = guessableCharacters.find(
      (char) => char.name.toLowerCase() === guessName.toLowerCase()
    );
    if (guessedCharacter) {
      setGuessedCharacters((prevGuesses) => [
        {
          name: guessedCharacter.name,
          character: guessedCharacter,
        },
        ...prevGuesses,
      ]);

      // Check if the guessed character is the correct answer
      if (answer && guessedCharacter.name === answer.name) {
        setIsDialogOpen(true); // Open the dialog
      }
    } else {
      alert('Character not found or not guessable!');
    }
  };

  // Function to handle hover over images
  const handleImageHover = (imageSrc) => {
    setEnlargedImageSrc(imageSrc);
  };

  // Function to handle mouse leave from images
  const handleImageMouseLeave = () => {
    setEnlargedImageSrc(null);
  };

  // Function to close the dialog
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  return (
    <div className="min-h-screen bg-palette-light-blue flex">
      {/* Main Content */}
      <div className="flex flex-col w-full">
        <header className="shadow w-full bg-palette-dark-blue">
          <div className="container mx-auto py-6 px-4">
            <h1 className="text-4xl font-bold text-center text-white">
              FF14 Guessing Game
            </h1>
          </div>
        </header>
        <div className="grid grid-cols-7 gap-2 h-full">
          <div className="py-8 px-2">
            {enlargedImageSrc ? (
              <img
                src={enlargedImageSrc}
                alt="Enlarged"
                className="w-auto h-auto rounded-lg shadow-lg border-4 border-gray-300 bg-white"
                onError={(e) => {
                  if (e.target.src.endsWith('image_1.png')) {
                    e.target.src = e.target.src.replace(
                      'image_1.png',
                      'image_1.jpg'
                    );
                  } else {
                    e.target.onerror = null;
                    e.target.src = '/default-character.png';
                  }
                }}
              />
            ) : (
              <div className="w-full h-1/6 flex items-center justify-center border-2 border-palette-lighter-blue bg-palette-dark-blue rounded-lg">
                <span className="text-center text-gray-500">
                  Hover over an image
                </span>
              </div>
            )}
          </div>
          <main className="col-span-5 container mx-auto py-8 px-4 flex-grow">
            <GuessInput
              onGuess={handleGuess}
              guessedCharacters={guessedCharacters}
              onImageHover={handleImageHover}
              onImageMouseLeave={handleImageMouseLeave}
            />
            {guessedCharacters.length > 0 && answer && (
              <GuessHistory
                guesses={guessedCharacters}
                answer={answer}
                onImageHover={handleImageHover}
                onImageMouseLeave={handleImageMouseLeave}
              />
            )}
            {yesterdayAnswer ? (
              <div className="text-palette-light-rose text-center">
                Yesterday's character was {yesterdayAnswer.name}
              </div>
            ) : (
              <div className="text-white">Loading yesterday's character...</div>
            )}
          </main>
          <div id="captions" className="h-fit col-span-1 p-4 m-4 border-2 border-palette-lighter-blue bg-palette-dark-blue rounded-lg">
            <h2 className="text-xl font-semibold mb-4 text-white">Captions</h2>
            <ul className="space-y-2">
              <li className="flex items-center">
                <span className="w-10 h-4 inline-block mr-2 match-cell"></span>
                <span className="text-white">Match: The attribute matches exactly.</span>
              </li>
              <li className="flex items-center">
                <span className="w-10 h-4 inline-block mr-2 partial-match-cell"></span>
                <span className="text-white">Partial Match: The attribute partially matches.</span>
              </li>
              <li className="flex items-center">
                <span className="w-10 h-4 inline-block mr-2 no-match-cell"></span>
                <span className="text-white">No Match: The attribute does not match.</span>
              </li>
              <li className="flex items-center">
                <img
                  src="/arrow_up.png"
                  alt="Arrow Up"
                  className="w-6 h-6 inline-block mr-2"
                />
                <span className="text-white">The answer's attribute value is higher.</span>
              </li>
              <li className="flex items-center">
                <img
                  src="/arrow_down.png"
                  alt="Arrow Down"
                  className="w-6 h-6 inline-block mr-2"
                />
                <span className="text-white">The answer's attribute value is lower.</span>
              </li>
            </ul>
          </div>
        </div>

        <footer className="shadow w-full bg-palette-dark-blue">
          <div className="container mx-auto py-4 px-4 text-center text-gray-500">
            contact@ff14dle.com
          </div>
        </footer>

        {/* Congratulations Dialog */}
        <CongratulationsDialog
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          numTries={guessedCharacters.length}
          answer={answer}
        />
      </div>
    </div>
  );
}

export default App;
